import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { X, Save, Loader2 } from 'lucide-react'
import { ruleService, CreateRuleData, UpdateRuleData } from '../../services/ruleService'
import { extractApiErrors } from '../../utils/errorHandler'
import {
  Rule,
  RuleCategory,
  RequestType,
  WorkCenterType,
  RuleValues,
  RuleConditions,
  REQUEST_TYPE_LABELS,
  WORK_CENTER_LABELS,
  RULE_CATEGORY_LABELS,
} from '../../types'

interface RuleFormModalProps {
  rule: Rule | null
  onClose: () => void
  onSuccess: () => void
}

const CATEGORY_OPTIONS: RuleCategory[] = [
  'DURATION',
  'ADVANCE_NOTICE',
  'CONCURRENCY',
  'BLOCKED_PERIOD',
  'ELIGIBILITY',
  'DEADLINE',
  'WORK_CENTER_LIMIT',
]

const REQUEST_TYPE_OPTIONS: RequestType[] = [
  'EXCEDENCIA_CORTA_VACACIONES',
  'EXCEDENCIA_VOLUNTARIA_ESPECIAL',
  'PREMIO_PERMANENCIA_5R',
  'LICENCIA_SIN_SUELDO',
  'DIAS_LIBRE_ELECCION',
  'VACACIONES_FIESTAS_CASEDA',
]

const WORK_CENTER_OPTIONS: WorkCenterType[] = [
  'N1',
  'N2',
  'N3',
  'N4_CELULOSA',
  'N4_FIBROSA',
  'PLISADO',
  'IMPRESORA',
  'CALIDAD',
  'LABORATORIO',
  'OTROS',
  'NANOPACK',
  'OTROS_JP',
]

const BLOCKED_PERIOD_TYPES = [
  'SEMANA_SANTA',
  'VACACIONES_TURNO',
  'VACACIONES_PLISADO',
  'VACACIONES_IMPRESORA',
  'VACACIONES_NANOPACK',
  'ARRANCADA',
  'PUENTE_FORAL',
  'NAVIDAD',
]

export function RuleFormModal({ rule, onClose, onSuccess }: RuleFormModalProps) {
  const isEditing = !!rule
  const [loading, setLoading] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Form state
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<RuleCategory>('DURATION')
  const [requestType, setRequestType] = useState<RequestType | ''>('')
  const [workCenter, setWorkCenter] = useState<WorkCenterType | ''>('')
  const [errorMessage, setErrorMessage] = useState('')
  const [priority, setPriority] = useState(0)
  const [sortOrder, setSortOrder] = useState(0)
  const [isActive, setIsActive] = useState(true)

  // Values state
  const [values, setValues] = useState<RuleValues>({})

  // Conditions state
  const [hasStopCondition, setHasStopCondition] = useState<boolean | null>(null)
  const [isExtensionCondition, setIsExtensionCondition] = useState<boolean | null>(null)
  const [daysRequestedCondition, setDaysRequestedCondition] = useState<number | ''>('')

  useEffect(() => {
    setIsAnimating(true)
    if (rule) {
      setCode(rule.code)
      setName(rule.name)
      setDescription(rule.description || '')
      setCategory(rule.category)
      setRequestType(rule.requestType || '')
      setWorkCenter(rule.workCenter || '')
      setErrorMessage(rule.errorMessage || '')
      setPriority(rule.priority)
      setSortOrder(rule.sortOrder)
      setIsActive(rule.isActive)
      setValues(rule.values)

      if (rule.conditions) {
        setHasStopCondition(rule.conditions.hasStop ?? null)
        setIsExtensionCondition(rule.conditions.isExtension ?? null)
        setDaysRequestedCondition(rule.conditions.daysRequested ?? '')
      }
    }
  }, [rule])

  const handleClose = () => {
    setIsAnimating(false)
    setTimeout(onClose, 150)
  }

  const updateValue = <K extends keyof RuleValues>(key: K, value: RuleValues[K] | '') => {
    setValues((prev) => {
      if (value === '' || value === undefined) {
        const { [key]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [key]: value }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!code.trim()) {
      toast.error('El código es requerido')
      return
    }
    if (!name.trim()) {
      toast.error('El nombre es requerido')
      return
    }

    // Build conditions
    const conditions: RuleConditions = {}
    if (hasStopCondition !== null) conditions.hasStop = hasStopCondition
    if (isExtensionCondition !== null) conditions.isExtension = isExtensionCondition
    if (daysRequestedCondition !== '') conditions.daysRequested = Number(daysRequestedCondition)

    try {
      setLoading(true)

      if (isEditing) {
        const updateData: UpdateRuleData = {
          code: code.toUpperCase(),
          name,
          description: description || null,
          category,
          requestType: requestType || null,
          workCenter: workCenter || null,
          values,
          conditions: Object.keys(conditions).length > 0 ? conditions : null,
          errorMessage: errorMessage || null,
          priority,
          sortOrder,
          isActive,
        }
        await ruleService.update(rule.id, updateData)
        toast.success('Regla actualizada correctamente')
      } else {
        const createData: CreateRuleData = {
          code: code.toUpperCase(),
          name,
          description: description || undefined,
          category,
          requestType: requestType || undefined,
          workCenter: workCenter || undefined,
          values,
          conditions: Object.keys(conditions).length > 0 ? conditions : undefined,
          errorMessage: errorMessage || undefined,
          priority,
          sortOrder,
          isActive,
        }
        await ruleService.create(createData)
        toast.success('Regla creada correctamente')
      }

      onSuccess()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  const renderValueFields = () => {
    switch (category) {
      case 'DURATION':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Días mínimos</label>
              <input
                type="number"
                value={values.minDays ?? ''}
                onChange={(e) => updateValue('minDays', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Días máximos</label>
              <input
                type="number"
                value={values.maxDays ?? ''}
                onChange={(e) => updateValue('maxDays', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Días exactos</label>
              <input
                type="number"
                value={values.exactDays ?? ''}
                onChange={(e) => updateValue('exactDays', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
          </div>
        )

      case 'ADVANCE_NOTICE':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Días mínimos de antelación</label>
              <input
                type="number"
                value={values.minDays ?? ''}
                onChange={(e) => updateValue('minDays', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
          </div>
        )

      case 'CONCURRENCY':
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="form-label">% máx plantilla</label>
              <input
                type="number"
                step="0.1"
                value={values.maxPercent ?? ''}
                onChange={(e) => updateValue('maxPercent', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="form-label">Mín. trabajadores</label>
              <input
                type="number"
                value={values.minWorkers ?? ''}
                onChange={(e) => updateValue('minWorkers', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Máx. por puesto</label>
              <input
                type="number"
                value={values.maxPerPosition ?? ''}
                onChange={(e) => updateValue('maxPerPosition', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Máx. concurrentes</label>
              <input
                type="number"
                value={values.maxConcurrent ?? ''}
                onChange={(e) => updateValue('maxConcurrent', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
          </div>
        )

      case 'ELIGIBILITY':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Mín. días a 5R total</label>
              <input
                type="number"
                value={values.minDaysAt5Shifts ?? ''}
                onChange={(e) => updateValue('minDaysAt5Shifts', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Mín. días últimos 10 años</label>
              <input
                type="number"
                value={values.minDaysLast10Years ?? ''}
                onChange={(e) => updateValue('minDaysLast10Years', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Mín. años antigüedad</label>
              <input
                type="number"
                value={values.minYearsOfService ?? ''}
                onChange={(e) => updateValue('minYearsOfService', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={values.requiresAt5Shifts ?? false}
                  onChange={(e) => updateValue('requiresAt5Shifts', e.target.checked || undefined)}
                  className="rounded"
                />
                <span className="text-sm">Requiere estar a 5R</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={values.notEnjoyed2Years ?? false}
                  onChange={(e) => updateValue('notEnjoyed2Years', e.target.checked || undefined)}
                  className="rounded"
                />
                <span className="text-sm">No disfrutado en 2 años</span>
              </label>
            </div>
          </div>
        )

      case 'DEADLINE':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Mes límite</label>
              <select
                value={values.month ?? ''}
                onChange={(e) => updateValue('month', e.target.value ? Number(e.target.value) : '')}
                className="input"
              >
                <option value="">Seleccionar</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <option key={m} value={m}>
                    {new Date(2024, m - 1).toLocaleString('es', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Día límite</label>
              <input
                type="number"
                value={values.day ?? ''}
                onChange={(e) => updateValue('day', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="1"
                max="31"
              />
            </div>
            <div>
              <label className="form-label">Días alt. antelación</label>
              <input
                type="number"
                value={values.alternativeDays ?? ''}
                onChange={(e) => updateValue('alternativeDays', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
          </div>
        )

      case 'WORK_CENTER_LIMIT':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Máx. por turno/puesto</label>
              <input
                type="number"
                value={values.maxPerTurnPosition ?? ''}
                onChange={(e) => updateValue('maxPerTurnPosition', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Máx. por día</label>
              <input
                type="number"
                value={values.maxPerDay ?? ''}
                onChange={(e) => updateValue('maxPerDay', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
            <div>
              <label className="form-label">Extra presecado</label>
              <input
                type="number"
                value={values.extraForPresecado ?? ''}
                onChange={(e) => updateValue('extraForPresecado', e.target.value ? Number(e.target.value) : '')}
                className="input"
                min="0"
              />
            </div>
          </div>
        )

      case 'BLOCKED_PERIOD':
        return (
          <div className="space-y-4">
            <div>
              <label className="form-label">Periodos bloqueados</label>
              <div className="flex flex-wrap gap-2">
                {BLOCKED_PERIOD_TYPES.map((period) => (
                  <label key={period} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-lg">
                    <input
                      type="checkbox"
                      checked={values.blockedPeriodTypes?.includes(period) ?? false}
                      onChange={(e) => {
                        const current = values.blockedPeriodTypes || []
                        if (e.target.checked) {
                          updateValue('blockedPeriodTypes', [...current, period])
                        } else {
                          updateValue('blockedPeriodTypes', current.filter((p) => p !== period))
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{period.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={values.allowedOnly ?? false}
                onChange={(e) => updateValue('allowedOnly', e.target.checked || undefined)}
                className="rounded"
              />
              <span className="text-sm">Solo permitido en estos periodos (invertir lógica)</span>
            </label>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-150 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        className={`relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-150 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Editar Regla' : 'Nueva Regla'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Basic info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Código *</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="input font-mono"
                  placeholder="ECV_DURATION"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Solo mayúsculas, números y guiones bajos
                </p>
              </div>
              <div>
                <label className="form-label">Nombre *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  placeholder="Duración Excedencia Corta"
                  required
                />
              </div>
            </div>

            <div>
              <label className="form-label">Descripción</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input"
                rows={2}
                placeholder="Descripción detallada de la regla..."
              />
            </div>

            {/* Category and types */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Categoría *</label>
                <select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value as RuleCategory)
                    setValues({}) // Reset values on category change
                  }}
                  className="input"
                  required
                >
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>
                      {RULE_CATEGORY_LABELS[cat]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Tipo de solicitud</label>
                <select
                  value={requestType}
                  onChange={(e) => setRequestType(e.target.value as RequestType | '')}
                  className="input"
                >
                  <option value="">Todos los tipos</option>
                  {REQUEST_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>
                      {REQUEST_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Centro de trabajo</label>
                <select
                  value={workCenter}
                  onChange={(e) => setWorkCenter(e.target.value as WorkCenterType | '')}
                  className="input"
                >
                  <option value="">Todos los centros</option>
                  {WORK_CENTER_OPTIONS.map((wc) => (
                    <option key={wc} value={wc}>
                      {WORK_CENTER_LABELS[wc]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Rule values */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                Valores de la regla
              </h3>
              {renderValueFields()}
            </div>

            {/* Conditions */}
            <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                Condiciones (opcional)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Tipo de puesto</label>
                  <select
                    value={hasStopCondition === null ? '' : hasStopCondition ? 'true' : 'false'}
                    onChange={(e) =>
                      setHasStopCondition(
                        e.target.value === '' ? null : e.target.value === 'true'
                      )
                    }
                    className="input"
                  >
                    <option value="">Sin condición</option>
                    <option value="true">Con parada</option>
                    <option value="false">Sin parada</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Es prórroga</label>
                  <select
                    value={isExtensionCondition === null ? '' : isExtensionCondition ? 'true' : 'false'}
                    onChange={(e) =>
                      setIsExtensionCondition(
                        e.target.value === '' ? null : e.target.value === 'true'
                      )
                    }
                    className="input"
                  >
                    <option value="">Sin condición</option>
                    <option value="true">Sí es prórroga</option>
                    <option value="false">No es prórroga</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Días solicitados</label>
                  <input
                    type="number"
                    value={daysRequestedCondition}
                    onChange={(e) =>
                      setDaysRequestedCondition(e.target.value ? Number(e.target.value) : '')
                    }
                    className="input"
                    placeholder="Ej: 7"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Error message */}
            <div>
              <label className="form-label">Mensaje de error</label>
              <input
                type="text"
                value={errorMessage}
                onChange={(e) => setErrorMessage(e.target.value)}
                className="input"
                placeholder="Mensaje que se mostrará si no cumple la regla"
              />
            </div>

            {/* Priority and order */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Prioridad</label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="input"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-gray-500 mt-1">Mayor = se evalúa primero</p>
              </div>
              <div>
                <label className="form-label">Orden visual</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value))}
                  className="input"
                  min="0"
                />
              </div>
              <div className="flex items-center col-span-2 sm:col-span-1 sm:pt-8">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Regla activa</span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-slate-700/50 border-t border-gray-200 dark:border-slate-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isEditing ? 'Guardar cambios' : 'Crear regla'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
